import React, { useState, ChangeEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import { Typography, Theme, Tab, Box, Button } from '@material-ui/core'
import messages from '@src/routes/LinkProfile/messages'
import { makeStyles } from '@material-ui/core/styles'
import ResponsiveModal, { ModalSizes } from '@src/components/ResponsiveModal'
import { ResponsiveModalBody } from '@src/components/ResponsiveModal/ResponsiveModal'
import { TabContext, TabList, TabPanel } from '@material-ui/lab'
import { helperImageMapping, ErpSystemIdType } from './utils'
import LiquidProgressiveImage from '@src/components/LiquidProgressiveImage'

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    paddingBottom: theme.spacing(7),
  },
  tabs: {},
  tab: {
    textTransform: 'none',
    fontSize: 18,
    minWidth: 85,
    fontWeight: 900,
    color: theme.palette.primary.main,
    opacity: 1,
    '&.Mui-selected': {
      color: '#000',
      borderBottom: '4px solid #000',
    },
    padding: theme.spacing(3),
  },
  img: {
    width: '100%',
  },
  imgContainer: {
    width: '500px',
    height: '330px',
    border: '1px solid #949494',
    margin: '0 auto',
    '@media (max-width: 600px)': {
      width: '100%',
      minHeight: '220px',
      height: 'auto',
    },
  },
  panelContainer: {
    marginTop: 24,
    '@media (max-width: 600px)': {
      paddingTop: '0',
      paddingBottom: '0',
    },
  },
  modalFooter: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: 32,
  },
  okayButton: {
    width: 144,
    '@media (max-width: 600px)': {
      width: '100%',
    },
  },
}))

export enum AffiliationHelperDialogType {
  NONE = '',
  CUSTOMER = 'customer',
  TRANSACTION = 'transaction',
  ORDER = 'order',
}

const customerTabs = [
  {
    key: 'orderConfirm',
    label: <FormattedMessage {...messages.ORDER_CONFIRMATION} />,
    imageKey: 'orderConfirmCustNumPath',
    alt: 'Link Profile Order Confirm Customer Numer',
  },
  {
    key: 'quote',
    label: <FormattedMessage id="QUOTE" defaultMessage="Quote" />,
    imageKey: 'quoteCustNumPath',
    alt: 'Link Profile Quote Customer Numer',
  },
  {
    key: 'invoice',
    label: <FormattedMessage id="INVOICE" defaultMessage="Invoice" />,
    imageKey: 'invoiceCustNumPath',
    alt: 'Link Profile Invoice Customer Numer',
  },
] as const

const transactionTabs = [
  {
    key: 'orderConfirm',
    label: <FormattedMessage {...messages.ORDER_CONFIRMATION} />,
    imageKey: 'orderConfirmOrderNumPath',
    alt: 'Link Profile Order Confirm Customer Numer',
  },
  {
    key: 'quote',
    label: <FormattedMessage id="QUOTE" defaultMessage="Quote" />,
    imageKey: 'quoteQuoteNumPath',
    alt: 'Link Profile Quote Customer Numer',
  },
  {
    key: 'invoice',
    label: <FormattedMessage id="INVOICE" defaultMessage="Invoice" />,
    imageKey: 'invoiceInvoiceNumPath',
    alt: 'Link Profile Invoice Customer Numer',
  },
] as const
const orderTabs = [
  {
    key: 'orderConfirm',
    label: <FormattedMessage {...messages.ORDER_CONFIRMATION} />,
    imageKey: 'orderConfirmOrderNumPath',
    alt: 'Link Profile Order Order Confirm Order Numer',
  },
  {
    key: 'invoice',
    label: <FormattedMessage id="INVOICE" defaultMessage="Invoice" />,
    imageKey: 'orderInvoiceOrderNumberPath',
    alt: 'Link Profile Order Invoice Order Numer',
  },
] as const

type TabKey = (typeof customerTabs)[number]['key']

const tabsMap = {
  [AffiliationHelperDialogType.CUSTOMER]: customerTabs,
  [AffiliationHelperDialogType.TRANSACTION]: transactionTabs,
  [AffiliationHelperDialogType.ORDER]: orderTabs,
}

const getTabs = (type: AffiliationHelperDialogType) =>
  tabsMap[type] || tabsMap[AffiliationHelperDialogType.CUSTOMER]

type AffiliationHelperDialogProps = {
  dialogType: AffiliationHelperDialogType
  handleClose: () => void
  erpSystemId: string
}

const HelperImagePanel: React.FC<{
  image: { src: string; placeholder: string }
  alt: string
  className?: string
  containerClass?: string
}> = ({ image, alt, className, containerClass }) => (
  <Box className={containerClass}>
    <LiquidProgressiveImage
      className={className}
      src={image.src}
      placeholder={image.placeholder}
      alt={alt}
    />
  </Box>
)

const AffiliationHelperDialog: React.FC<AffiliationHelperDialogProps> = ({
  dialogType,
  handleClose,
  erpSystemId,
}) => {
  const classes = useStyles()
  const tabs = getTabs(dialogType)
  const [tabIndex, setTabIndex] = useState<TabKey>('orderConfirm')

  const handleChange = (event: ChangeEvent<{}>, newValue: string) => {
    setTabIndex(newValue as TabKey)
  }

  const helperData = helperImageMapping(erpSystemId as ErpSystemIdType)
  if (!helperData) {
    return null
  }
  const {
    images,
    customerDesc,
    transactionNumberOrderNumberDesc,
    transactionNumberQuoteReferDesc,
    transactionNumberInvoiceNumDesc,
    orderNumberDesc,
  } = helperData

  return (
    <>
      <ResponsiveModal
        size={ModalSizes.Large}
        renderTitle={() => (
          <Typography variant="h2" component="h2">
            {dialogType === AffiliationHelperDialogType.CUSTOMER && (
              <FormattedMessage {...messages.FIND_YOUR_CUSTOMER_NUMBER} />
            )}
            {dialogType === AffiliationHelperDialogType.TRANSACTION && (
              <FormattedMessage {...messages.FIND_YOUR_TRANSACTION_NUMBER} />
            )}
            {dialogType === AffiliationHelperDialogType.ORDER && (
              <FormattedMessage {...messages.FIND_YOUR_ORDER_NUMBER} />
            )}
          </Typography>
        )}
        open={Boolean(dialogType)}
        onClose={() => {
          setTabIndex('orderConfirm')
          handleClose()
        }}
      >
        <ResponsiveModalBody>
          <Box>
            <Box>
              {dialogType === AffiliationHelperDialogType.CUSTOMER && (
                <Box>
                  <FormattedMessage {...messages[customerDesc]} />
                </Box>
              )}
              {dialogType === AffiliationHelperDialogType.ORDER && (
                <Box marginBottom={4}>
                  <FormattedMessage
                    {...messages[orderNumberDesc]}
                    values={{
                      orderNumber: (
                        <Typography
                          style={{ fontWeight: 700 }}
                          component={'span'}
                        >
                          <FormattedMessage
                            id="ORDER_NUMBER"
                            defaultMessage="Order Number"
                          />
                        </Typography>
                      ),
                      orderConfirmation: (
                        <Typography
                          style={{
                            fontWeight: 700,
                            textTransform: 'lowercase',
                          }}
                          component={'span'}
                        >
                          <FormattedMessage
                            id="ORDER_CONFIRMATION"
                            defaultMessage="Order Confirmation"
                          />
                        </Typography>
                      ),
                      invoice: (
                        <Typography
                          style={{
                            fontWeight: 700,
                            textTransform: 'lowercase',
                          }}
                          component={'span'}
                        >
                          <FormattedMessage
                            id="INVOICE"
                            defaultMessage="Invoice"
                          />
                        </Typography>
                      ),
                    }}
                  />
                </Box>
              )}
              {dialogType === AffiliationHelperDialogType.TRANSACTION && (
                <>
                  <Box marginBottom={4}>
                    <FormattedMessage
                      {...messages[transactionNumberOrderNumberDesc]}
                      values={{
                        orderNumber: (
                          <Typography
                            style={{ fontWeight: 700 }}
                            component={'span'}
                          >
                            <FormattedMessage
                              id="ORDER_NUMBER"
                              defaultMessage="Order Number"
                            />
                          </Typography>
                        ),
                      }}
                    />
                  </Box>
                  <Box marginBottom={4}>
                    <FormattedMessage
                      {...messages[transactionNumberQuoteReferDesc]}
                      values={{
                        quoteReference: (
                          <Typography
                            style={{ fontWeight: 700 }}
                            component={'span'}
                          >
                            <FormattedMessage
                              id="QUOTE_REFERENCE"
                              defaultMessage="Quote Reference"
                            />
                          </Typography>
                        ),
                      }}
                    />
                  </Box>
                  <Box>
                    <FormattedMessage
                      {...messages[transactionNumberInvoiceNumDesc]}
                      values={{
                        invoiceNumber: (
                          <Typography
                            style={{ fontWeight: 700 }}
                            component={'span'}
                          >
                            <FormattedMessage
                              id="INVOICE_NUMBER"
                              defaultMessage="Invoice Number"
                            />
                          </Typography>
                        ),
                      }}
                    />
                  </Box>
                </>
              )}
              <TabContext value={tabIndex}>
                <TabList
                  onChange={handleChange}
                  aria-label={`documentation-tabs-${tabIndex}`}
                  data-testid={'documentation-tabs'}
                  className={classes.tabs}
                  variant="scrollable"
                >
                  {tabs.map((tab) => (
                    <Tab
                      label={tab.label}
                      value={tab.key}
                      className={classes.tab}
                      key={tab.key}
                    />
                  ))}
                </TabList>
                {tabs.map((tab) => (
                  <TabPanel
                    value={tab.key}
                    key={tab.key}
                    className={classes.panelContainer}
                  >
                    <HelperImagePanel
                      image={images[tab.imageKey]}
                      alt={tab.alt}
                      className={classes.img}
                      containerClass={classes.imgContainer}
                    />
                  </TabPanel>
                ))}
              </TabContext>
            </Box>
          </Box>
          <Box className={classes.modalFooter}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.okayButton}
              onClick={() => {
                setTabIndex('orderConfirm')
                handleClose()
              }}
            >
              <FormattedMessage id="OKAY" defaultMessage="Okay" />
            </Button>
          </Box>
        </ResponsiveModalBody>
      </ResponsiveModal>
    </>
  )
}

export default AffiliationHelperDialog
